import React, { useState, useEffect } from "react";
import Image from "next/legacy/image";
import { Modal } from "components/modal/modal";
import { ModalSize } from "components/modal/modal.types";
import { Button } from "components/atoms/button/button";
import { updateNickName } from "services/api/users/user-services";
import { useAuth } from "stores/auth/auth-context";
import * as Sentry from "@sentry/nextjs";
import trackEvent from "services/mixpanel/mixpanel";
import { nicknameEvents } from "services/mixpanel/nickname-events";
import {
  ModalTitle,
  ModalSubTitle,
  InputLabel,
  NickNameInput,
  HelperText,
  ModalContent,
  SuccessImageContainer,
  SuccessTitle,
  SuccessSubTitle,
  ButtonWrapper,
  SkipButton
} from "./set-nickname-modal.styles";
import { SetNickNameModalProps } from "./set-nickname-modal.types";

enum ModalStep {
  INPUT = "INPUT",
  SUCCESS = "SUCCESS"
}

const NICKNAME_POPUP_SKIPPED_KEY = "nickNamePopupSkipped";
const SKIP_COOLDOWN = 60 * 60 * 1000; // 1 hour

const isValidNameFormat = (text: string): boolean => {
  return /^[A-Za-z.'-]+(?: [A-Za-z.'-]+)*$/.test(text);
};

const SetNickNameModal: React.FC<SetNickNameModalProps> = ({
  isOpen,
  primaryUser,
  onSuccess,
  onSkip
}) => {
  const {
    user: { userData, setUserData }
  } = useAuth();
  const [nickName, setNickName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [step, setStep] = useState<ModalStep>(ModalStep.INPUT);

  // Track when modal is shown
  useEffect(() => {
    if (isOpen) {
      trackEvent({
        eventName: nicknameEvents.nicknamePopupShown
      });
    }
  }, [isOpen]);

  const getErrorType = (): "length" | "character" | null => {
    if (!hasInteracted || nickName.trim().length === 0) return null;
    const trimmed = nickName.trim();
    if (trimmed.length < 3) return "length";
    if (!isValidNameFormat(trimmed)) return "character";
    return null;
  };

  const errorType = getErrorType();
  const shouldShowError = errorType !== null;
  const canSave =
    nickName.trim().length >= 3 &&
    isValidNameFormat(nickName.trim()) &&
    !isLoading;

  const getErrorText = () => {
    if (errorType === "length") return "Tulis minimal 3 karakter, ya.";
    if (errorType === "character")
      return "Isi nama tanpa simbol dan angka, ya.";
    return "";
  };

  const handleSubmit = async () => {
    if (!canSave) return;

    // Track save button clicked
    trackEvent({
      eventName: nicknameEvents.nicknameSaveButtonClicked
    });

    setIsLoading(true);

    try {
      await updateNickName(primaryUser.access_token, {
        nickName: nickName.trim()
      });

      // Update localStorage with the new nick_name
      setUserData({
        ...userData,
        primary_user: {
          ...userData.primary_user,
          nick_name: nickName.trim()
        }
      });

      setStep(ModalStep.SUCCESS);
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNickName(e.target.value);
    setHasInteracted(true);
  };

  const handleFinish = () => {
    onSuccess();
  };

  const handleSkip = () => {
    // Track skip button clicked
    trackEvent({
      eventName: nicknameEvents.nicknameRemindMeLaterButtonClicked
    });

    try {
      // Store skip timestamp in localStorage
      const skipData = {
        timestamp: Date.now(),
        expiresAt: Date.now() + SKIP_COOLDOWN
      };
      localStorage.setItem(
        NICKNAME_POPUP_SKIPPED_KEY,
        JSON.stringify(skipData)
      );

      if (onSkip) {
        onSkip();
      } else {
        onSuccess();
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeOnClickOutside={false}
      closeIconProperties={{ isHidden: true }}
      size={ModalSize.Narrow}
    >
      <ModalContent>
        {step === ModalStep.INPUT ? (
          <>
            <ModalTitle>Kenalan dulu, yuk!</ModalTitle>
            <ModalSubTitle>
              {`Biar Guru Juara bisa manggil nama kamu\ndengan benar di kelas`}
            </ModalSubTitle>

            <InputLabel>Nama panggilan</InputLabel>
            <NickNameInput
              type="text"
              placeholder="Masukkan nama panggilan"
              value={nickName}
              onChange={handleInputChange}
              maxLength={50}
            />

            <HelperText $isError={shouldShowError}>
              {shouldShowError
                ? getErrorText()
                : "Pakai nama asli soalnya nanti nggak bisa diganti lagi."}
            </HelperText>

            <ButtonWrapper>
              <Button
                variant="primary"
                onClick={handleSubmit}
                isLoading={isLoading}
                disabled={!canSave}
              >
                Simpan
              </Button>
              <SkipButton
                onClick={handleSkip}
                disabled={isLoading}
                type="button"
              >
                Nanti aja, deh
              </SkipButton>
            </ButtonWrapper>
          </>
        ) : (
          <>
            <SuccessImageContainer>
              <Image
                src="/assets/coco_success.svg"
                alt="Success"
                width={256}
                height={256}
              />
            </SuccessImageContainer>

            <SuccessTitle>Senang kenal {nickName}!</SuccessTitle>
            <SuccessSubTitle>
              {
                "Sekarang Guru Juara bisa manggil nama kamu\ndengan benar di kelas"
              }
            </SuccessSubTitle>

            <ButtonWrapper>
              <Button variant="primary" onClick={handleFinish}>
                Selesai
              </Button>
            </ButtonWrapper>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export { SetNickNameModal };
